import { useState } from 'react';
import type { ReleaseCodeMode } from '@/types/release.types';

interface ReleaseCodePanelProps {
  code: string;
  secondsRemaining: number;
  totalSeconds: number;
  onCopy?: () => void;
}

function formatCountdown(totalSeconds: number) {
  const safeSeconds = Math.max(0, totalSeconds);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function ReleaseCodePanel({ code, secondsRemaining, totalSeconds, onCopy }: ReleaseCodePanelProps) {
  const [mode, setMode] = useState<ReleaseCodeMode>('pin');
  const digits = code.split('');
  const progressPct = totalSeconds > 0 ? Math.min(100, Math.max(0, (secondsRemaining / totalSeconds) * 100)) : 0;

  return (
    <section className="bg-primary text-white rounded-4 shadow-kiosk-md p-3 p-lg-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <span className="text-label-sm text-white-50 d-flex align-items-center gap-1">
            <i className="bi bi-key-fill" />
            Physical Authentication Key
          </span>
          <h3 className="text-label-lg text-white mb-0">Release Passcode</h3>
        </div>

        <div className="bg-white bg-opacity-10 rounded-pill p-1 d-flex gap-1">
          <button
            type="button"
            onClick={() => setMode('pin')}
            aria-pressed={mode === 'pin'}
            className={[
              'btn btn-sm rounded-pill border-0 text-label-sm px-3',
              mode === 'pin' ? 'bg-white text-primary' : 'text-white',
            ].join(' ')}
          >
            <i className="bi bi-grid-3x3-gap-fill me-1" />
            PIN OTP
          </button>
          {/* <button
            type="button"
            onClick={() => setMode('qr')}
            aria-pressed={mode === 'qr'}
            className={[
              'btn btn-sm rounded-pill border-0 text-label-sm px-3',
              mode === 'qr' ? 'bg-white text-primary' : 'text-white',
            ].join(' ')}
          >
            <i className="bi bi-qr-code me-1" />
            Scan QR
          </button> */}
        </div>
      </div>

      {mode === 'pin' ? (
        <div className="bg-white bg-opacity-10 rounded-3 p-3 p-lg-4 d-flex flex-column align-items-center">
          <div className="d-flex align-items-center gap-2 flex-wrap justify-content-center">
            {digits.map((digit, index) => (
              <span key={index} className="d-flex align-items-center">
                <span
                  className="bg-white bg-opacity-25 rounded-3 d-flex align-items-center justify-content-center fw-bold"
                  style={{ width: 'clamp(32px, 8vw, 44px)', height: 'clamp(40px, 10vw, 56px)', fontSize: 'clamp(18px, 5vw, 28px)' }}
                >
                  {digit}
                </span>
                {index === 2 && (
                  <span className="mx-1 fw-bold" style={{ fontSize: 20 }}>
                    ·
                  </span>
                )}
              </span>
            ))}
          </div>
          <span className="text-body-sm text-white-50 d-flex align-items-center gap-2 mt-3 text-center">
            <i className="bi bi-hand-index-thumb" />
            Tap this 6-digit code into the kiosk&apos;s physical keypad
          </span>
        </div>
      ) : (
        <div className="bg-white bg-opacity-10 rounded-3 p-3 p-lg-4 d-flex flex-column align-items-center text-center">
          <div
            className="bg-white rounded-3 d-flex align-items-center justify-content-center"
            style={{ width: 160, height: 160 }}
            role="img"
            aria-label="Print release QR code"
          >
            <i className="bi bi-qr-code text-primary" style={{ fontSize: 96 }} />
          </div>
          <span className="text-body-sm text-white-50 mt-3">Hold this screen under the kiosk&apos;s scanner beam</span>
        </div>
      )}

      <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2 mt-3">
        <button
          type="button"
          onClick={onCopy}
          className="btn bg-white text-primary tap-target active-press d-flex align-items-center justify-content-center gap-2 text-label-md"
        >
          <i className="bi bi-clipboard-check" />
          Copy 6-Digit Code
        </button>
        <span className="text-label-sm text-white-50 text-sm-end">
          Expires in {formatCountdown(secondsRemaining)}
        </span>
      </div>

      <div className="bg-white bg-opacity-25 rounded-pill mt-2 overflow-hidden" style={{ height: 6 }}>
        <div
          className="bg-white rounded-pill h-100"
          style={{ width: `${progressPct}%`, transition: 'width 1s linear' }}
        />
      </div>
    </section>
  );
}
